import { useState, useEffect, useCallback } from 'react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { CandidateAccordionList } from '@/components/CandidateAccordionList';
import { Search, Users, MessageSquare, Send, Briefcase, AlertCircle, User, MapPin, Mail, Phone, FileText, Award, Loader2, Clock, Eye, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { AwardsDisplay } from '@/components/AwardsDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CandidateSearch() {
  const [user, setUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [employerJobs, setEmployerJobs] = useState([]);
  const [filters, setFilters] = useState({ keyword: '', location: '', radius_miles: 'any', distance_ref: 'either' });
  const [sortBy, setSortBy] = useState('best_match');
  const [selectedJobId, setSelectedJobId] = useState('');

  // Smart View Profile state
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Resume Viewer dialog (access approved)
  const [resumeViewerOpen, setResumeViewerOpen] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [loadingResume, setLoadingResume] = useState(false);
  const [resumeDocTab, setResumeDocTab] = useState('resume');

  // Access Request dialog (no access yet)
  const [accessRequestOpen, setAccessRequestOpen] = useState(false);
  const [accessNote, setAccessNote] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);

  // Invite to Apply Modal State
  const [selectedCandidateForInvite, setSelectedCandidateForInvite] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState(false);
  const [appContext, setAppContext] = useState({});
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  // Fetch canonical application relationships for the visible candidates (cross-surface source of truth).
  const loadAppContext = useCallback(async (cands) => {
    const ids = (cands || []).map(c => c.user_id).filter(Boolean);
    if (ids.length === 0) { setAppContext({}); return; }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/employer/candidates/application-context`,
        { candidate_ids: ids }, { withCredentials: true });
      setAppContext(res.data || {});
    } catch { setAppContext({}); }
  }, []);

  useEffect(() => { loadAppContext(candidates); }, [candidates, loadAppContext]);

  const fetchData = async () => {
    try {
      const [userRes, candidatesInitRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/candidates/search`, { withCredentials: true })
      ]);
      setUser(userRes.data);

      // Try to default location to employer HQ
      try {
        const empRes = await axios.get(`${BACKEND_URL}/api/employer/locations`, { withCredentials: true });
        const locs = empRes.data?.locations || empRes.data || [];
        const hq = locs.find(l => l.is_active) || locs[0];
        if (hq?.city && hq?.state) {
          const hqLoc = `${hq.city}, ${hq.state}`;
          setFilters(prev => ({ ...prev, location: hqLoc }));
          // Re-search with HQ location + Unlimited (default) — show all candidates with distance computed
          const params = new URLSearchParams({ location: hqLoc });
          const locRes = await axios.get(`${BACKEND_URL}/api/candidates/search?${params.toString()}`, { withCredentials: true });
          setCandidates(locRes.data);
        } else {
          setCandidates(candidatesInitRes.data);
        }
      } catch {
        setCandidates(candidatesInitRes.data);
      }

      try {
        const jobsRes = await axios.get(`${BACKEND_URL}/api/jobs/my-posts/list`, { withCredentials: true });
        const activeJobs = (jobsRes.data || []).filter(j => j.status === 'active' && !j.deletedAt);
        setEmployerJobs(activeJobs);
        // Preselect "Rank Against" job from ?rankJob=<jobId> (Find Talent for this Role).
        const rankJob = new URLSearchParams(window.location.search).get('rankJob');
        if (rankJob && activeJobs.some(j => j.job_id === rankJob)) {
          setSelectedJobId(rankJob);
          searchCandidates({ jobId: rankJob });
        }
      } catch (err) { /* no jobs */ }

      try {
        const favRes = await axios.get(`${BACKEND_URL}/api/candidates/favorites/list`, { withCredentials: true });
        setFavorites(new Set(favRes.data.map(c => c.user_id)));
      } catch (err) { /* no favorites */ }
    } catch (error) {
      console.error('Failed to load candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const searchCandidates = async (overrides = {}) => {
    const f = { ...filters, ...(overrides.filters || {}) };
    const sv = overrides.sort !== undefined ? overrides.sort : sortBy;
    const jid = overrides.jobId !== undefined ? overrides.jobId : selectedJobId;
    setLoading(true);
    setSearchError(false);
    try {
      const params = new URLSearchParams();
      if (f.keyword) params.append('keyword', f.keyword);
      if (f.location) params.append('location', f.location);
      const isUnlimited = f.radius_miles === 'any';
      if (jid) params.append('job_id', jid);
      if (sv) params.append('sort_by', sv);
      if (f.distance_ref && f.distance_ref !== 'either') params.append('distance_ref', f.distance_ref);
      if (!isUnlimited && f.location) {
        params.append('radius_miles', f.radius_miles || '25');
      }
      const response = await axios.get(`${BACKEND_URL}/api/candidates/search?${params.toString()}`, { withCredentials: true });
      setCandidates(response.data);
    } catch (error) {
      setSearchError(true);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (candidateId) => {
    try {
      if (favorites.has(candidateId)) {
        await axios.delete(`${BACKEND_URL}/api/candidates/${candidateId}/favorite`, { withCredentials: true });
        setFavorites(prev => { const s = new Set(prev); s.delete(candidateId); return s; });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${BACKEND_URL}/api/candidates/${candidateId}/favorite`, {}, { withCredentials: true });
        setFavorites(prev => new Set(prev).add(candidateId));
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // ===== SMART VIEW FULL PROFILE FLOW =====
  const handleViewProfile = useCallback(async (candidate) => {
    setSelectedCandidate(candidate);
    setCheckingAccess(true);

    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/messages/resume-access/check/${candidate.user_id}`,
        { withCredentials: true }
      );

      if (data.has_access) {
        // Access approved — fetch resume and open viewer
        setLoadingResume(true);
        setResumeViewerOpen(true);
        try {
          const resumeRes = await axios.get(
            `${BACKEND_URL}/api/messages/resume-access/${data.access_request_id}/resume`,
            { withCredentials: true }
          );
          setResumeData(resumeRes.data);
        } catch (err) {
          toast.error('Failed to load resume');
          setResumeViewerOpen(false);
        } finally {
          setLoadingResume(false);
        }
      } else if (data.pending) {
        // Request already pending — inform user
        toast.info('Your access request is pending approval from this candidate.', { duration: 4000 });
      } else {
        // No access — open request dialog
        setAccessNote('');
        setAccessRequestOpen(true);
      }
    } catch (err) {
      toast.error('Failed to check access status');
    } finally {
      setCheckingAccess(false);
    }
  }, []);

  const handleOpenApplication = useCallback((candidate, app) => {
    if (app?.application_id) navigate(`/applications/${app.application_id}`);
  }, [navigate]);

  const handleSendAccessRequest = async () => {
    if (!selectedCandidate) return;
    setSendingRequest(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/messages/resume-access/request-direct`,
        { candidate_user_id: selectedCandidate.user_id, note: accessNote.trim() || null },
        { withCredentials: true }
      );
      if (res.data?.application_exists && res.data?.application_id) {
        // Candidate already applied — no access request needed; open the application package.
        setAccessRequestOpen(false);
        setAccessNote('');
        setSelectedCandidate(null);
        toast.info('This candidate already applied — opening their application.');
        navigate(`/applications/${res.data.application_id}`);
        return;
      }
      toast.success('Access request sent! The candidate will be notified.');
      setAccessRequestOpen(false);
      setAccessNote('');
      setSelectedCandidate(null);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('already')) {
        toast.info('A request is already pending for this candidate.');
        setAccessRequestOpen(false);
      } else {
        toast.error(detail || 'Failed to send access request');
      }
    } finally {
      setSendingRequest(false);
    }
  };

  // ===== INVITE TO APPLY FLOW =====
  const handleInviteToApply = useCallback((candidate) => {
    if (employerJobs.length === 0) {
      toast.error('You need at least one active job posting to invite candidates.');
      return;
    }
    setSelectedCandidateForInvite(candidate);
    setSelectedJobIds(new Set());
    setInviteMessage('');
    setInviteDialogOpen(true);
  }, [employerJobs.length]);

  const toggleJobSelection = (jobId) => {
    setSelectedJobIds(prev => {
      const s = new Set(prev);
      s.has(jobId) ? s.delete(jobId) : s.add(jobId);
      return s;
    });
  };

  const handleSendInvites = async () => {
    if (!selectedCandidateForInvite || selectedJobIds.size === 0) return;
    setSendingInvite(true);
    try {
      const jobIdArray = Array.from(selectedJobIds);
      await Promise.all(jobIdArray.map(jobId =>
        axios.post(`${BACKEND_URL}/api/invites`, {
          job_seeker_id: selectedCandidateForInvite.user_id,
          job_id: jobId,
          message: inviteMessage.trim() || null
        }, { withCredentials: true })
      ));
      toast.success(`Invitation${jobIdArray.length > 1 ? 's' : ''} sent for ${jobIdArray.length} position${jobIdArray.length > 1 ? 's' : ''}!`);
      setInviteDialogOpen(false);
      setInviteMessage('');
      setSelectedJobIds(new Set());
      setSelectedCandidateForInvite(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') searchCandidates(); };

  if (loading && candidates.length === 0) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        </div>
      </Layout>
    );
  }

  const candidateName = selectedCandidate?.personal_info?.full_name || 'Candidate';
  const candidateFirst = candidateName.split(' ')[0];

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={Users}
          title="Talent"
          subtitle="Veterans transitioning into your region."
        />

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6" data-testid="candidate-search-filters">
          {/* ROW 1 — Rank Against (structured, auto-refresh) + keyword search (Enter/Search only) */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-2.5">
            {employerJobs.length > 0 && (
              <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap hidden sm:block">Rank against</label>
                <select
                  data-testid="job-context-select"
                  value={selectedJobId}
                  onChange={(e) => { setSelectedJobId(e.target.value); searchCandidates({ jobId: e.target.value }); }}
                  className="h-11 w-full sm:w-[220px] rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-brand/30"
                >
                  <option value="">All open positions</option>
                  {employerJobs.map(j => (
                    <option key={j.job_id} value={j.job_id}>{j.title}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="keyword-search-input"
                placeholder="Skills, MOS, certifications, experience..."
                value={filters.keyword}
                onChange={(e) => setFilters({...filters, keyword: e.target.value})}
                onKeyDown={handleKeyPress}
                className="h-11 pl-10"
              />
            </div>
            <Button
              data-testid="search-candidates-btn"
              onClick={() => searchCandidates()}
              disabled={loading}
              className="h-11 px-6 bg-brand hover:bg-brand-hover text-white rounded-lg shrink-0 w-full sm:w-auto"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
          {/* ROW 2 — STRUCTURED FILTERS: location + radius + sort; auto-refresh; aligned (h-11) */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2.5">
            <div className="relative flex-1 min-w-[160px] sm:flex-none sm:w-[240px]">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="location-search-input"
                placeholder="City, State"
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
                onKeyDown={handleKeyPress}
                className="h-11 pl-10"
              />
            </div>
            <select data-testid="radius-filter" value={filters.radius_miles}
              onChange={(e) => { setFilters(prev => ({ ...prev, radius_miles: e.target.value })); searchCandidates({ filters: { radius_miles: e.target.value } }); }}
              className="h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30 sm:w-[150px]">
              <option value="10">Within 10 mi</option>
              <option value="25">Within 25 mi</option>
              <option value="50">Within 50 mi</option>
              <option value="100">Within 100 mi</option>
              <option value="any">Unlimited</option>
            </select>
            <div className="flex items-center gap-2 h-11">
              <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap">Distance from</span>
              <select data-testid="distance-ref-filter" value={filters.distance_ref} title="Distance from"
                onChange={(e) => { setFilters(prev => ({ ...prev, distance_ref: e.target.value })); searchCandidates({ filters: { distance_ref: e.target.value } }); }}
                className="h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30 w-[190px] shrink-0">
                <option value="either">Home or Duty Station</option>
                <option value="home">Home</option>
                <option value="base">Duty Station</option>
              </select>
            </div>
            <div className="flex items-center gap-2 h-11">
              <ArrowUpDown className="w-4 h-4 text-slate-400 shrink-0" />
              <select
                data-testid="sort-candidates-select"
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); searchCandidates({ sort: e.target.value }); }}
                className="h-11 flex-1 sm:flex-none sm:w-[160px] rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30"
              >
                <option value="best_match">Best fit</option>
                <option value="best_core_fit">Best Core Fit</option>
                <option value="nearest">Nearest</option>
              </select>
            </div>
          </div>
        </div>

        {loading && candidates.length > 0 && (
          <div className="flex items-center justify-center gap-3 py-4 mb-4 rounded-lg bg-brand/5 border border-brand/15" data-testid="candidate-scoring-status">
            <Loader2 className="w-5 h-5 animate-spin text-brand" />
            <span className="text-sm font-medium text-slate-700">
              {selectedJobId
                ? 'Calculating Match scores for this job\u2026'
                : 'Finding matching service members\u2026'}
            </span>
          </div>
        )}

        {searchError && !loading && (
          <div className="flex items-center justify-between gap-3 py-3 px-4 mb-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="candidate-search-error">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>We couldn't calculate Match scores just now. Please try again.</span>
            </div>
            <Button
              data-testid="retry-search-btn"
              onClick={searchCandidates}
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
            >
              Retry
            </Button>
          </div>
        )}

        <CandidateAccordionList
          candidates={candidates}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onViewProfile={handleViewProfile}
          onInvite={handleInviteToApply}
          hasActiveJobs={employerJobs.length > 0}
          appContext={appContext}
          rankJobId={selectedJobId}
          onOpenApplication={handleOpenApplication}
        />

        {/* ===== RESUME VIEWER DIALOG (access approved) ===== */}
        <Dialog open={resumeViewerOpen} onOpenChange={(open) => { if (!open) { setResumeViewerOpen(false); setResumeData(null); setSelectedCandidate(null); } }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="resume-viewer-dialog">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <FileText className="w-5 h-5 text-brand" />
                {candidateName} — Application Documents
              </DialogTitle>
            </DialogHeader>

            {loadingResume ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
              </div>
            ) : resumeData ? (
              <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                {/* Profile-level access shows the canonical resume/profile only.
                    Generic cover letters are never exposed at the profile level (Prompt 4). */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <ResumePreview resume={resumeData} />
                </div>
              </div>
            ) : (
              <div className="py-12 text-center text-slate-500">Resume data not available.</div>
            )}
          </DialogContent>
        </Dialog>

        {/* ===== ACCESS REQUEST DIALOG (no access yet) ===== */}
        <Dialog open={accessRequestOpen} onOpenChange={(open) => { if (!open) { setAccessRequestOpen(false); setAccessNote(''); setSelectedCandidate(null); } }}>
          <DialogContent className="max-w-lg" data-testid="access-request-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <Eye className="w-5 h-5 text-brand" />
                Request Full Profile Access
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Ask {candidateFirst} to share their full resume and profile with you
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {selectedCandidate && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-brand" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">{candidateName}</h4>
                      {selectedCandidate.personal_info?.city && (
                        <p className="text-xs text-slate-500">
                          {selectedCandidate.personal_info.city}, {selectedCandidate.personal_info.state}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-slate-900 font-medium text-sm">
                  Note to Candidate <span className="text-slate-400">(optional)</span>
                </Label>
                <Textarea
                  data-testid="access-request-note"
                  value={accessNote}
                  onChange={(e) => setAccessNote(e.target.value)}
                  className="mt-2 min-h-[80px] text-sm"
                  placeholder="Introduce yourself or explain why you'd like to view their full profile..."
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  data-testid="cancel-access-request-btn"
                  onClick={() => { setAccessRequestOpen(false); setAccessNote(''); setSelectedCandidate(null); }}
                  variant="outline"
                  size="sm"
                  className="border-slate-300 text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="send-access-request-btn"
                  onClick={handleSendAccessRequest}
                  disabled={sendingRequest}
                  size="sm"
                  className="bg-brand hover:bg-brand-hover text-white"
                >
                  {sendingRequest ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Request Access
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ===== INVITE TO APPLY DIALOG ===== */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="max-w-xl" data-testid="invite-to-apply-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <Send className="w-5 h-5 text-brand" />
                Reach out
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Select which position(s) to invite {selectedCandidateForInvite?.personal_info?.full_name?.split(' ')[0] || 'this candidate'} to apply for
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {selectedCandidateForInvite && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-brand" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 text-sm">
                        {selectedCandidateForInvite.personal_info?.full_name}
                      </h4>
                      {selectedCandidateForInvite.military_roles?.[0] && (
                        <p className="text-xs text-slate-500">
                          {selectedCandidateForInvite.military_roles[0].branch} · {selectedCandidateForInvite.military_roles[0].rank}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-slate-900 font-medium text-sm flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Select Position(s) <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-slate-500 mt-1 mb-3">Choose one or more active positions for this invitation</p>

                {employerJobs.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>You need an active job posting to invite candidates.</span>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-3">
                    {(() => {
                      const appliedJobIds = new Set(
                        (appContext[selectedCandidateForInvite?.user_id]?.applications || []).map(a => a.job_id)
                      );
                      return employerJobs.map((job) => {
                        const alreadyApplied = appliedJobIds.has(job.job_id);
                        return (
                          <label
                            key={job.job_id}
                            className={`flex items-start gap-3 p-2 rounded-md transition-colors ${
                              alreadyApplied
                                ? 'opacity-60 cursor-not-allowed bg-slate-50 border border-transparent'
                                : selectedJobIds.has(job.job_id)
                                  ? 'bg-brand/5 border border-brand/30 cursor-pointer'
                                  : 'hover:bg-slate-50 border border-transparent cursor-pointer'
                            }`}
                            data-testid={`job-checkbox-${job.job_id}`}
                          >
                            <Checkbox
                              checked={selectedJobIds.has(job.job_id)}
                              disabled={alreadyApplied}
                              onCheckedChange={() => !alreadyApplied && toggleJobSelection(job.job_id)}
                              className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900 text-sm truncate">{job.title}</p>
                                {alreadyApplied && (
                                  <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`already-applied-${job.job_id}`}>
                                    Already applied
                                  </span>
                                )}
                              </div>
                              {job.location && <p className="text-xs text-slate-500 truncate">{job.location}</p>}
                            </div>
                          </label>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-slate-900 font-medium text-sm">
                  Invitation message <span className="text-slate-400">(optional)</span>
                </Label>
                <Textarea
                  data-testid="invite-message-textarea"
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  className="mt-2 min-h-[80px] text-sm"
                  placeholder="Add a note to your invitation..."
                />
              </div>

              <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
                <Button
                  data-testid="cancel-invite-btn"
                  onClick={() => { setInviteDialogOpen(false); setInviteMessage(''); setSelectedJobIds(new Set()); setSelectedCandidateForInvite(null); }}
                  variant="outline"
                  size="sm"
                  className="border-slate-300 text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="send-invite-btn"
                  onClick={handleSendInvites}
                  disabled={selectedJobIds.size === 0 || sendingInvite}
                  size="sm"
                  className="bg-brand hover:bg-brand-hover text-white"
                >
                  {sendingInvite ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {selectedJobIds.size === 0
                    ? 'Select a Job First'
                    : `Send Invite${selectedJobIds.size > 1 ? `s (${selectedJobIds.size})` : ''}`
                  }
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

/** Inline resume preview — reused for viewer dialog */
function ResumePreview({ resume }) {
  const pi = resume.personal_info || {};

  // Core Competencies from selected KSAs
  const coreCompetencies = (() => {
    if (!resume.military_roles?.length) return [];
    const all = [];
    for (const role of resume.military_roles) {
      for (const item of (role.selected_abilities || [])) {
        const n = typeof item === 'object' ? item.element_name : item;
        if (n && !all.includes(n)) all.push(n);
      }
      for (const item of (role.selected_skills || [])) {
        const n = typeof item === 'object' ? item.element_name : item;
        if (n && !all.includes(n)) all.push(n);
      }
      for (const item of (role.selected_knowledge || [])) {
        const n = typeof item === 'object' ? item.element_name : item;
        if (n && !all.includes(n)) all.push(n);
      }
    }
    const seen = new Set();
    return all.filter(c => { const l = c.toLowerCase().trim(); if (seen.has(l)) return false; seen.add(l); return true; }).slice(0, 15);
  })();

  const parseBullets = (text) => {
    if (!text) return [];
    // Reject raw HTML/error content from provider failures
    const lower = text.toLowerCase();
    if (lower.includes('<!doctype') || lower.includes('<html') || lower.includes('ratelimiterror') ||
        lower.includes('rate_limit_exceeded') || (text.match(/<[a-zA-Z/][^>]*>/g) || []).length > 3) {
      return [];
    }
    return text.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#')).map(l => l.replace(/^[•\-\*▪◦●]\s*/, ''));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="resume-preview-content">
      {/* Header */}
      <div className="border-b border-slate-200 pb-4 mb-4">
        <h3 className="text-xl font-bold text-slate-900">{pi.full_name || 'Candidate'}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
          {pi.city && (
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4 flex-shrink-0" /> {pi.city}{pi.state ? `, ${pi.state}` : ''}</span>
          )}
          {pi.email && (
            <span className="flex items-center gap-1"><Mail className="w-4 h-4 flex-shrink-0" /> {pi.email}</span>
          )}
          {pi.phone && (
            <span className="flex items-center gap-1"><Phone className="w-4 h-4 flex-shrink-0" /> {pi.phone}</span>
          )}
        </div>
      </div>

      {resume.professional_summary && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Summary</h4>
          <p className="text-sm text-slate-700 leading-relaxed">{resume.professional_summary}</p>
        </div>
      )}

      {coreCompetencies.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Competencies</h4>
          <p className="text-sm text-slate-700 leading-relaxed">{coreCompetencies.join(' \u2022 ')}</p>
        </div>
      )}

      {(() => {
        const wa = resume.military_roles?.find(r => r.mos_data?.work_activities?.length > 0)?.mos_data?.work_activities;
        if (!wa || wa.length === 0) return null;
        return (
          <div className="mb-5" data-testid="work-activities-section">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Responsibilities</h4>
            <ul className="space-y-0.5">
              {wa.slice(0, 6).map((a, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{a.name}</span></li>
              ))}
            </ul>
          </div>
        );
      })()}

      {(() => {
        const tt = resume.military_roles?.find(r => r.mos_data?.tools_technology?.length > 0)?.mos_data?.tools_technology;
        if (!tt || tt.length === 0) return null;
        return (
          <div className="mb-5" data-testid="tools-technology-section">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tools & Systems</h4>
            <p className="text-sm text-slate-700 leading-relaxed">{tt.slice(0, 6).map(t => t.category).join(' \u2022 ')}</p>
          </div>
        );
      })()}

      {resume.military_roles?.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Military Experience</h4>
          {resume.military_roles.map((role, idx) => {
            const bullets = parseBullets(role.ai_summary_text);
            return (
              <div key={idx} className="mb-3 pl-3 border-l-2 border-brand">
                <p className="font-medium text-slate-900">{role.rank}, {role.branch}</p>
                <p className="text-sm text-slate-600">{role.mos_code} - {sanitizeMosTitle(role.mos_title, role.rank)}</p>
                <p className="text-xs text-slate-400 font-mono">{role.location} | {role.start_month}/{role.start_year} - {role.end_month}/{role.end_year}</p>
                {bullets.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="text-slate-400 mt-0.5">&bull;</span><span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {resume.civilian_experience?.some(e => (e.job_title || '').trim() || (e.company || '').trim() || (e.description || '').trim()) && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Experience</h4>
          {resume.civilian_experience.map((exp, idx) => (
            <div key={idx} className="mb-3">
              <p className="font-medium text-slate-900">{exp.job_title}</p>
              <p className="text-sm text-slate-600">{exp.company} - {exp.location}</p>
              <p className="text-xs text-slate-400 font-mono">{exp.start_month}/{exp.start_year} - {exp.is_current ? 'Present' : `${exp.end_month}/${exp.end_year}`}</p>
              {exp.description && <p className="mt-1 text-sm text-slate-700">{exp.description}</p>}
            </div>
          ))}
        </div>
      )}

      {resume.education?.some(e => (e.degree_or_cert || '').trim() || (e.institution || '').trim()) && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Education</h4>
          {resume.education.map((edu, idx) => (
            <div key={idx} className="mb-2">
              <p className="font-medium text-slate-900">{edu.degree_or_cert}</p>
              <p className="text-sm text-slate-500">{edu.institution}{edu.end_year ? ` (${edu.end_year})` : ''}</p>
            </div>
          ))}
        </div>
      )}

      <AwardsDisplay resume={resume} />
    </div>
  );
}

export default CandidateSearch;
